import { useState, useEffect } from 'react';
import { Plus, Filter, Search, TrendingUp, TrendingDown, Loader2, GitCompare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import CreateScenarioDialog from '../components/planning/CreateScenarioDialog';
import ScenarioWorkspace from '../components/planning/ScenarioWorkspace';
import ScenarioComparison from '../components/planning/ScenarioComparison';
import { planningApi } from '../api/planning';

export default function PlanningHub() {
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedScenario, setSelectedScenario] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterOpportunity, setFilterOpportunity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState([]);

  useEffect(() => {
    fetchScenarios();
  }, [filterOpportunity, filterStatus]);

  const fetchScenarios = async () => {
    setLoading(true);
    const oppId = filterOpportunity === 'all' ? null : filterOpportunity;
    const status = filterStatus === 'all' ? null : filterStatus;
    
    const result = await planningApi.getScenarios(oppId, status);
    if (Array.isArray(result)) {
      setScenarios(result);
    } else {
      setScenarios([]);
    }
    setLoading(false);
  };

  const handleCreateScenario = (newScenario) => {
    setScenarios([newScenario, ...scenarios]);
    setSelectedScenario(newScenario);
    setShowCreateDialog(false);
  };

  const handleScenarioUpdate = (updatedScenario) => {
    setScenarios(scenarios.map(s => 
      s.id === updatedScenario.id ? updatedScenario : s
    ));
    setSelectedScenario(updatedScenario);
  };

  const handleScenarioDelete = (scenarioId) => {
    setScenarios(scenarios.filter(s => s.id !== scenarioId));
    if (selectedScenario?.id === scenarioId) {
      setSelectedScenario(null);
    }
  };

  const formatCurrency = (cents) => {
    if (!cents) return '$0';
    const value = Math.abs(cents) / 100;
    const formatted = value >= 1000000 
      ? `$${(value / 1000000).toFixed(1)}M`
      : value >= 1000
      ? `$${(value / 1000).toFixed(0)}K`
      : `$${value.toFixed(0)}`;
    return cents < 0 ? `-${formatted}` : formatted;
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: <Badge className="bg-gray-500" title="Not yet activated - changes are saved but not applied to parts">Draft</Badge>,
      active: <Badge className="bg-green-600" title="All scenario changes have been applied to part forecasts">Active</Badge>,
      partially_active: <Badge className="bg-amber-600" title="Some parts match scenario values, but scenario has been modified since activation">Partially Active</Badge>,
      not_active: <Badge className="bg-red-500" title="Was active but underlying parts have changed - no longer matches">Inactive</Badge>,
      archived: <Badge className="bg-gray-400" title="Scenario has been archived and hidden from active use">Archived</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const filteredScenarios = scenarios.filter(scenario => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return scenario.name.toLowerCase().includes(query) ||
             scenario.opportunity_name.toLowerCase().includes(query);
    }
    return true;
  });

  // Calculate summary stats
  const stats = {
    total: scenarios.length,
    draft: scenarios.filter(s => s.status === 'draft').length,
    active: scenarios.filter(s => s.status === 'active').length,
    totalImpact: scenarios.reduce((sum, s) => sum + (s.revenue_delta_cents || 0), 0)
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Planning Hub</h1>
            <p className="text-sm text-gray-600 mt-1">
              Unified scenario planning for strategic decision-making
            </p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Scenario
          </Button>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Scenarios</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-600">{stats.draft}</div>
              <div className="text-sm text-gray-600">Draft</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.active}</div>
              <div className="text-sm text-gray-600">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className={`text-2xl font-bold ${stats.totalImpact >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(stats.totalImpact)}
              </div>
              <div className="text-sm text-gray-600">Total Impact</div>
            </CardContent>
          </Card>
        </div>

        {/* Compare Mode Toggle */}
        {scenarios.length >= 2 && (
          <div className="mt-4">
            <Button
              onClick={() => {
                setCompareMode(!compareMode);
                if (!compareMode) {
                  setSelectedForComparison([]);
                  setSelectedScenario(null);
                }
              }}
              variant={compareMode ? "default" : "outline"}
              size="sm"
              title="Select 2-3 scenarios to compare side-by-side"
            >
              <GitCompare className="h-4 w-4 mr-2" />
              {compareMode ? 'Exit Compare Mode' : 'Compare Scenarios'}
            </Button>
            {compareMode && selectedForComparison.length >= 2 && (
              <Button
                onClick={() => {
                  // Do nothing, comparison view is shown automatically
                }}
                size="sm"
                className="ml-2 bg-blue-600 hover:bg-blue-700"
              >
                Comparing {selectedForComparison.length} scenarios
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Scenario List */}
        <div className="w-80 bg-white border-r border-gray-200 flex flex-col">
          {/* Search & Filters */}
          <div className="p-4 border-b border-gray-200 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search scenarios..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Scenario List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : filteredScenarios.length === 0 ? (
              <div className="p-6 text-center">
                <p className="text-gray-500 mb-4">No scenarios found</p>
                <Button
                  onClick={() => setShowCreateDialog(true)}
                  variant="outline"
                  size="sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Scenario
                </Button>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredScenarios.map((scenario) => (
                  <div
                    key={scenario.id}
                    onClick={() => {
                      if (compareMode) {
                        // Toggle selection in compare mode
                        if (selectedForComparison.find(s => s.id === scenario.id)) {
                          setSelectedForComparison(selectedForComparison.filter(s => s.id !== scenario.id));
                        } else if (selectedForComparison.length < 3) {
                          setSelectedForComparison([...selectedForComparison, scenario]);
                        }
                      } else {
                        setSelectedScenario(scenario);
                      }
                    }}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      compareMode && selectedForComparison.find(s => s.id === scenario.id)
                        ? 'bg-green-50 border-l-4 border-green-600'
                        : selectedScenario?.id === scenario.id 
                        ? 'bg-blue-50 border-l-4 border-blue-600' 
                        : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      {compareMode && (
                        <input
                          type="checkbox"
                          checked={!!selectedForComparison.find(s => s.id === scenario.id)}
                          onChange={() => {}}
                          className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500 mt-0.5 mr-3"
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{scenario.name}</h3>
                        <p className="text-xs text-gray-600 truncate mt-1">{scenario.opportunity_name}</p>
                      </div>
                      {getStatusBadge(scenario.status)}
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-600 mt-2">
                      <span>{scenario.part_count} parts</span>
                      <div className={`flex items-center gap-1 font-semibold ${
                        scenario.revenue_delta_cents >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {scenario.revenue_delta_cents >= 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        {formatCurrency(scenario.revenue_delta_cents)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main Workspace */}
        <div className="flex-1 overflow-hidden">
          {compareMode && selectedForComparison.length >= 2 ? (
            <ScenarioComparison
              scenarios={selectedForComparison}
              onClose={() => {
                setCompareMode(false);
                setSelectedForComparison([]);
              }}
            />
          ) : selectedScenario ? (
            <ScenarioWorkspace
              scenario={selectedScenario}
              onUpdate={handleScenarioUpdate}
              onDelete={handleScenarioDelete}
            />
          ) : (
            <div className="flex items-center justify-center h-full p-8">
              <div className="max-w-2xl">
                <div className="text-center mb-8">
                  <div className="text-gray-400 mb-4">
                    <Filter className="h-16 w-16 mx-auto" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {scenarios.length === 0 ? 'No Scenarios Yet' : 'Select a scenario to view details'}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {scenarios.length === 0 
                      ? 'Create your first scenario to start planning' 
                      : 'Choose a scenario from the list or create a new one'}
                  </p>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Scenario
                  </Button>
                </div>
                
                {/* How It Works Panel */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-left">
                  <h4 className="font-semibold text-blue-900 mb-3">How S&OP Scenario Planning Works</h4>
                  <ol className="space-y-2 text-sm text-blue-800">
                    <li className="flex items-start">
                      <span className="font-bold mr-2">1.</span>
                      <span><strong>Create a Scenario:</strong> Choose an opportunity and select the parts you want to analyze</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold mr-2">2.</span>
                      <span><strong>Model Changes:</strong> Adjust prices and yearly volumes to test different strategies</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold mr-2">3.</span>
                      <span><strong>Compare Impact:</strong> See revenue impact compared to baseline forecasts</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-bold mr-2">4.</span>
                      <span><strong>Activate Best Option:</strong> Once decided, set the scenario as the active opportunity planning basis</span>
                    </li>
                  </ol>
                  
                  <div className="mt-4 pt-4 border-t border-blue-300">
                    <p className="text-xs text-blue-700">
                      <strong>Tip:</strong> Scenarios stay as drafts until activated. Activation updates opportunity rollups and keeps part master forecasts unchanged.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <CreateScenarioDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleCreateScenario}
      />
    </div>
  );
}
